import React, { useState, useEffect } from 'react';
import { useLaunchComputeResource } from '../hooks/launchComputeResource';
import { useAuth } from 'react-oidc-context';

const Computer = () => {
  const { launchComputeResource, isLoading, error } = useLaunchComputeResource();
  const auth = useAuth();
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('');
  const [countdown, setCountdown] = useState(0);
  const [sessionUrl, setSessionUrl] = useState(null);
  const [sessionStartTime, setSessionStartTime] = useState(null);
  const [remainingTime, setRemainingTime] = useState(0);

  // Load session from localStorage on mount
  useEffect(() => {
    const savedSession = localStorage.getItem('computer_session');
    if (savedSession) {
      try {
        const session = JSON.parse(savedSession);
        const startTime = new Date(session.startTime).getTime();
        const now = Date.now();
        const elapsed = now - startTime;
        const thirtyMinutes = 30 * 60 * 1000; // 30 minutes in milliseconds
        
        if (elapsed < thirtyMinutes) {
          setSessionUrl(session.url);
          setSessionStartTime(startTime); // Use timestamp, not Date object
        } else {
          // Session expired, remove it
          localStorage.removeItem('computer_session');
        }
      } catch (err) {
        console.error('Error loading session:', err);
        localStorage.removeItem('computer_session');
      }
    }
  }, []);

  // Update remaining time every second
  useEffect(() => {
    if (!sessionStartTime) return;

    const updateRemainingTime = () => {
      const now = Date.now();
      const elapsed = now - sessionStartTime;
      const thirtyMinutes = 30 * 60 * 1000; // 30 minutes in milliseconds
      const remaining = Math.max(0, thirtyMinutes - elapsed);
      setRemainingTime(remaining);

      if (remaining === 0) {
        // Session expired
        setSessionUrl(null);
        setSessionStartTime(null);
        localStorage.removeItem('computer_session');
      }
    };

    updateRemainingTime();
    const interval = setInterval(updateRemainingTime, 1000);

    return () => clearInterval(interval);
  }, [sessionStartTime]);

  // Function to check if a string is a valid IP address
  const isValidIP = (str) => {
    const ipRegex = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
    return ipRegex.test(str);
  };

  // Accept IP, IP:port, or full URL
  const isIpWithOptionalPort = (str) => {
    const ipPortRegex = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)(?::\d+)?$/;
    return ipPortRegex.test(str);
  };

  const isHttpUrl = (str) => /^https?:\/\//i.test(str);

  const normalizeToUrl = (value) => {
    if (!value) return null;
    const trimmed = String(value).trim();
    if (isHttpUrl(trimmed)) {
      // Ensure we use HTTP, not HTTPS
      return trimmed.replace(/^https:/i, 'http:');
    }
    if (isIpWithOptionalPort(trimmed)) return `http://${trimmed}`;
    return null;
  };

  // Function to check if a URL is accessible
  const checkUrlAccessibility = async (url) => {
    try {
      // Ensure URL is HTTP, not HTTPS
      const httpUrl = url.replace(/^https:/i, 'http:');
      console.log('[Computer] Checking URL accessibility:', httpUrl);
      
      // Use a timeout wrapper for fetch
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);
      
      try {
        // Try a simple GET request - even if CORS blocks it, we can detect if server is up
        const response = await fetch(httpUrl, { 
          method: 'GET',
          mode: 'no-cors',
          signal: controller.signal,
          cache: 'no-cache',
          redirect: 'follow'
        });
        clearTimeout(timeoutId);
        // With no-cors, we can't read response but no error means server responded
        console.log('[Computer] Server responded (no-cors mode)');
        return true;
      } catch (fetchError) {
        clearTimeout(timeoutId);
        console.log('[Computer] Fetch error:', fetchError.name, fetchError.message);
        
        // AbortError means timeout - server not ready
        if (fetchError.name === 'AbortError') {
          return false;
        }
        
        // Network errors might mean server is down, but CORS errors mean server is up
        // In no-cors mode, CORS errors are opaque, so we can't distinguish
        // For now, assume any non-abort error means we should try connecting
        return true;
      }
    } catch (error) {
      console.log('[Computer] URL check error:', error);
      return false;
    }
  };

  // Function to wait and check connection
  const waitAndCheckConnection = async (serverUrl, maxAttempts = 24, interval = 5000) => {
    // Ensure we use HTTP, not HTTPS
    const url = serverUrl.replace(/^https:/i, 'http:');
    let attempts = 0;
    
    const checkInterval = setInterval(async () => {
      attempts++;
      setCountdown(Math.max(0, (maxAttempts - attempts) * 5));
      
      const isAccessible = await checkUrlAccessibility(url);
      
      if (isAccessible) {
        clearInterval(checkInterval);
        setIsConnecting(false);
        setConnectionStatus('সংযোগ সফল! নতুন ট্যাবে খোলা হচ্ছে...');
        // Ensure we use HTTP, not HTTPS
        const httpUrl = url.replace(/^https:/i, 'http:');
        console.log('[Computer] Opening in new tab:', httpUrl);
        
        // Save session info
        const sessionData = {
          url: httpUrl,
          startTime: new Date().toISOString()
        };
        localStorage.setItem('computer_session', JSON.stringify(sessionData));
        setSessionUrl(httpUrl);
        setSessionStartTime(Date.now());
        
        // Open in new tab
        window.open(httpUrl, '_blank', 'noopener,noreferrer');
        
        // Clear status after a moment
        setTimeout(() => {
          setConnectionStatus('');
        }, 2000);
        return;
      }
      
      if (attempts >= maxAttempts) {
        clearInterval(checkInterval);
        setIsConnecting(false);
        setConnectionStatus('কম্পিউটার সেশনে সংযোগ করতে পারিনি');
        setCountdown(0);
      }
    }, interval);
  };

  const handleReconnect = () => {
    if (sessionUrl) {
      window.open(sessionUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const formatRemainingTime = (ms) => {
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const handleStartSession = async () => {
    // Align login check with menu: use react-oidc auth.isAuthenticated
    const token = auth?.user?.id_token || '';
    if (!auth?.isAuthenticated || !token || token.trim() === '') {
      setConnectionStatus('কম্পিউটার সেশন শুরু করতে প্রথমে লগইন করুন');
      return;
    }

    try {
      setConnectionStatus('কম্পিউটার সেশন শুরু হচ্ছে...');
      console.log('[Computer] Launch request starting. Token present:', !!token, 'Auth isAuthenticated:', auth?.isAuthenticated);
      const result = await launchComputeResource(token);
      console.log('[Computer] Launch response raw result:', result, 'Type:', typeof result);
      
      // Normalize result to a URL if it's a valid IP, IP:port, or already a URL
      const serverUrl = normalizeToUrl(result);
      if (serverUrl) {
        setConnectionStatus('সার্ভার প্রস্তুত হচ্ছে, ৩০ সেকেন্ড অপেক্ষা করুন...');
        setCountdown(30);
        setIsConnecting(true);

        // Wait 30 seconds before starting connection checks
        setTimeout(() => {
          setConnectionStatus('সংযোগ পরীক্ষা করা হচ্ছে...');
          waitAndCheckConnection(serverUrl);
        }, 30000);
        
      } else if (typeof result === 'string') {
        // Handle error messages
        if (result.includes('not enough credit')) {
          setConnectionStatus('অপর্যাপ্ত ক্রেডিট - দয়া করে আপনার অ্যাকাউন্টে ক্রেডিট যোগ করুন');
        } else if (result.includes('something went wrong')) {
          setConnectionStatus('কিছু সমস্যা হয়েছে - দয়া করে আবার চেষ্টা করুন');
        } else {
          setConnectionStatus('ত্রুটি: ' + result);
        }
      } else {
        setConnectionStatus('অপ্রত্যাশিত প্রতিক্রিয়া');
      }
    } catch (err) {
      console.error('[Computer] Launch error:', err);
      setConnectionStatus('ত্রুটি: ' + (err.message || 'কম্পিউটার সেশন শুরু করতে পারিনি'));
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
      <div className="max-w-6xl mx-auto">
        {/* Header Section */}
        <div className="text-center mb-8 pt-8">
          <h1 className="text-4xl font-bold text-gray-800 mb-4">
            কম্পিউটার শিখুন
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            ব্যবহারযোগ্য সফটওয়্যার তৈরি করতে শিখুন
          </p>
        </div>

        {/* Start Button */}
        <div className="text-center mt-12">
          <button 
            onClick={handleStartSession}
            disabled={isLoading || isConnecting || (sessionUrl && remainingTime > 0)}
            className={`text-white text-xl font-semibold py-4 px-8 rounded-lg transition-colors duration-200 shadow-lg hover:shadow-xl ${
              isLoading || isConnecting || (sessionUrl && remainingTime > 0)
                ? 'bg-gray-400 cursor-not-allowed' 
                : 'bg-blue-600 hover:bg-blue-700'
            }`}
          >
            {isLoading || isConnecting ? (
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white mr-3"></div>
                {isLoading ? 'শুরু হচ্ছে...' : 'সংযোগ হচ্ছে...'}
              </div>
            ) : (
              'কম্পিউটার সেশন শুরু করুন'
            )}
          </button>
        </div>

        {/* Reconnect Button - Shows for 30 minutes after first connection */}
        {sessionUrl && remainingTime > 0 && (
          <div className="text-center mt-6">
            <div className="bg-green-50 border-2 border-green-200 rounded-lg p-6 max-w-md mx-auto shadow-md">
              <p className="text-green-800 font-semibold mb-3">
                সেশন সক্রিয়
              </p>
              <p className="text-gray-600 text-sm mb-4">
                বাকি সময়: <span className="font-bold text-green-600">{formatRemainingTime(remainingTime)}</span>
              </p>
              <button
                onClick={handleReconnect}
                className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200 shadow-md hover:shadow-lg"
              >
                কম্পিউটার সেশনে সংযোগ করুন
              </button>
            </div>
          </div>
        )}

        {/* Status Messages */}
        {(connectionStatus || countdown > 0) && (
          <div className="text-center mt-6">
            <div className="bg-white rounded-lg shadow-md p-4 max-w-md mx-auto">
              <p className="text-gray-700 mb-2">{connectionStatus}</p>
              {countdown > 0 && (
                <div className="text-blue-600 font-semibold">
                  {countdown} সেকেন্ড বাকি
                </div>
              )}
            </div>
          </div>
        )}

        {/* Error Display */}
        {error && (
          <div className="text-center mt-6">
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg max-w-md mx-auto">
              <p className="font-semibold">ত্রুটি:</p>
              <p>{error}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Computer;
